import type { TrackingAdapterSummary, TrackingConnectionResult, TrackingIntegration } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { toConfigPayload, type ConfigFormValues } from '../../../policies/config-form-values.policy';
import { installIntegration, testIntegrationConnection, updateIntegration } from '../gps.api';
import type { AdapterCatalogueState } from './use-adapter-catalogue';

export interface IntegrationFormValues extends ConfigFormValues {
  displayName: string;
  pollingIntervalSeconds?: number | null;
}

interface UseIntegrationSheetArgs {
  catalogue: AdapterCatalogueState;
  /** The adapter being installed (`?adapter=`), or undefined when editing. */
  adapterKey: string | undefined;
  /** The installation being edited (its Settings tab), or undefined when installing. */
  integration: TrackingIntegration | undefined;
  onDone: (saved: TrackingIntegration) => void;
}

/** Install or edit one GPS installation: test the connection, save. Failures stay inline next to the form (CLAUDE.md → Feedback). */
export function useIntegrationSheet({ catalogue, adapterKey, integration, onDone }: UseIntegrationSheetArgs) {
  const toast = useToast();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TrackingConnectionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wantedKey = adapterKey ?? integration?.adapterKey;
  const adapter: TrackingAdapterSummary | undefined =
    catalogue.status === 'loaded' ? catalogue.adapters.find((candidate) => candidate.key === wantedKey) : undefined;

  async function test(values: IntegrationFormValues): Promise<void> {
    if (!adapter) return;
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(
        await testIntegrationConnection({ adapterKey: adapter.key, config: toConfigPayload(adapter.fields, values), integrationId: integration?.id }),
      );
    } catch (err) {
      setTestResult({ ok: false, reason: apiErrorReason(err, 'The test could not be run') });
    } finally {
      setTesting(false);
    }
  }

  async function save(values: IntegrationFormValues): Promise<void> {
    if (!adapter) return;
    setSubmitting(true);
    setError(null);
    const config = toConfigPayload(adapter.fields, values);
    const pollingIntervalSeconds = adapter.capabilities.includes('polling') ? values.pollingIntervalSeconds : undefined;
    try {
      const saved = integration
        ? await updateIntegration(integration.id, { displayName: values.displayName, config, pollingIntervalSeconds })
        : await installIntegration({ adapterKey: adapter.key, displayName: values.displayName, config, pollingIntervalSeconds });
      toast.success(integration ? `${values.displayName} updated` : `${values.displayName} installed`);
      onDone(saved);
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save the integration. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return {
    adapter,
    integration,
    isLoading: catalogue.status === 'loading',
    loadFailed: catalogue.status === 'error',
    testing,
    testResult,
    submitting,
    error,
    test,
    save,
  };
}
